
import { useContext, useEffect, useState } from 'react';
import { Star, Phone, Mail, MapPin, Globe, Building2, Users, Calendar, Package, Warehouse, Award, TrendingUp } from 'lucide-react';
import { FaLanguage, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import ProductCard from '../../Components/buyer/ProductCard';
import ReviewHistoryContainer from '../../Components/buyer/ReviewHistoryContainer';
import { Link, useNavigate } from 'react-router-dom';
import { LoginContext } from "../../Contexts/LoginContext";
import axios from 'axios';
import { showToast } from '../../Components/utils/ShowToast';
import Loader from '../../Components/utils/Loader';
import ScrollableRow from '../../Components/utils/ScrollableRow';
import { formatFullAddress } from '../../Components/utils/formatAddress';
import { formatCurrency, formatCurrencyDual, calculateSellerAmount, transformProduct, orderSellerFeePercent } from '../../data/constants';

const SellerDashboard = () => {
  const navigate = useNavigate();
  const { isLogin, user, feesObj, currency, exchangeRate } = useContext(LoginContext);
  const [sellerDetails, setSellerDetails] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [b2bProfile, setB2bProfile] = useState(null);
  const [b2bListings, setB2bListings] = useState([]);
  const [isLoadingB2B, setIsLoadingB2B] = useState(true);

  useEffect(() => {
    const fetchSellerDetails = async () => {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/seller/${user.sellerId._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSellerDetails(response.data.seller);
      } catch (error) {
        console.log(error);
        showToast("Something went wrong", "error");
      } finally {
        setIsLoading(false);
      }
    };

    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/seller/product`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const transformedProducts = response.data.products?.map(transformProduct);
        setProducts(transformedProducts);
      } catch (error) {
        console.log(error);
        showToast("Failed to fetch products", "error");
      } finally {
        setIsLoadingProducts(false);
      }
    };

    const fetchOrders = async () => {
      setIsLoadingOrders(true);
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/seller/current`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data.orders);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoadingOrders(false);
      }
    };

    const fetchB2BProfile = async () => {
      setIsLoadingB2B(true);
      const token = localStorage.getItem('token');
      try {
        const profileRes = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/seller/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const profile = profileRes.data?.profile || null;
        setB2bProfile(profile);

        if (profile?.status === 'approved') {
          const listingRes = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/seller/listings`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setB2bListings(listingRes.data?.listings || []);
        } else {
          setB2bListings([]);
        }
      } catch (error) {
        setB2bProfile(null);
        setB2bListings([]);
      } finally {
        setIsLoadingB2B(false);
      }
    };

    if (isLogin && user.role === "seller") {
      fetchSellerDetails();
      fetchProducts();
      fetchOrders();
      fetchB2BProfile();
    }
  }, [isLogin, user]);

  const activeStatuses = ['Active', 'Out for Delivery', 'Ready for Pickup', 'Delivered', 'Picked Up', 'Past Due'];
  const recentActiveOrders = orders
    .filter(order => activeStatuses.includes(order.currentStatus))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const getStatusStyle = (status) => {
    const statusStyles = {
      'Active': 'ordersStatus ordersStatusActive',
      'Out for Delivery': 'ordersStatus ordersStatusActive',
      'Ready for Pickup': 'ordersStatus ordersStatusActive',
      'Delivered': 'ordersStatus ordersStatusActive',
      'Picked Up': 'ordersStatus ordersStatusActive',
      'Past Due': 'ordersStatus ordersStatusPastDue',
      'InDispute': 'ordersStatus ordersStatusNeedResolution',
      'OnHold': 'ordersStatus ordersStatusNeedResolution',
      'Completed': 'ordersStatus ordersStatusCompleted',
      'Resolved': 'ordersStatus ordersStatusCompleted',
      'Cancelled': 'ordersStatus ordersStatusCancelled',
    };
    return statusStyles[status] || 'ordersStatus';
  };

  const amountToGet = (summary) => {
    const orderAmount = ((Number(summary.price) || 0) * (Number(summary.quantity) || 0)) + (Number(summary.deliveryFee) || 0) + (Number(summary.addedServicesTotal) || 0);
    return calculateSellerAmount(orderAmount, orderSellerFeePercent(summary, feesObj.seller));
  };

  const navigateTo = (order) => {
    navigate(`/sellerOrderDetailPage/${order._id}`);
  };

  const reviewsSortedByDate = [...(sellerDetails?.reviews || [])].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );
  const recentReviewsForDashboard = reviewsSortedByDate.slice(0, 5);
  const mappedReviews = recentReviewsForDashboard.map((review) => ({
    id: review._id,
    image: sellerDetails.profileImage,
    title: sellerDetails.displayName,
    sellerName: sellerDetails.displayName,
    reviewerName: review.userId.username,
    overallRating: review.overallRating,
    ratings: review.ratings,
    comment: review.comment,
    images: review.images || [],
    tipPaid: false,
    tipAmount: 0,
  }));

  const canCreateB2B = b2bProfile?.status === 'approved';
  const isBusiness = canCreateB2B;
  const sellerCountryName = sellerDetails?.sellerAddress?.country?.name || sellerDetails?.sellerAddress?.country?.label || 'N/A';
  const sellerCityName = sellerDetails?.sellerAddress?.city || 'N/A';
  const sellerLocationText = [sellerCityName, sellerCountryName].filter((p) => p && p !== 'N/A').join(', ');
  const accountLocationFull = formatFullAddress(user?.buyerAddress) || '';

  if (isLoading || !sellerDetails || !user) return <Loader type='simpleMax' />;

  return (
    <div className="dashboardVendorDetailContainer">
      <section className='mainSection'>
        <h1 className='pageMainHeading'>Dashboard</h1>
        <div className="dashboardVendorDetailNewSection">
          <div className="vendorDetailLeftSection">
            {/* ── Profile Card ── */}
            <div className="dashboardVendorDetailCardContainer">
              <div className="dashboardVendorDetailCard">
                <div className="dashboardSellerInfoSection">
                  <div className="dashboardSellerProfileImageContainer">
                    <img
                      src={sellerDetails?.profileImage}
                      alt=""
                      className="dashboardSellerProfileImage"
                      style={isBusiness ? { width: '70px', height: '70px' } : undefined}
                    />
                  </div>
                  <div className="dashboardSellerDetailsContainer">
                    {isBusiness ? (
                      <>
                        <p className="dashboardCompanyName">{b2bProfile?.companyName}</p>
                        <span className="dashboardBusinessBadge">
                          <Building2 size={12} /> {b2bProfile?.natureOfOrg}
                        </span>
                        <p className="dashboardSellerLocation">{sellerLocationText}</p>
                      </>
                    ) : (
                      <>
                        <p>{sellerDetails?.displayName}</p>
                        <p className="dashboardSellerLocation">{sellerLocationText}</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="dashboardSellerDescription">
                  <p>{sellerDetails?.description}</p>
                </div>

                {isBusiness && (
                  <div className="dashboardQuickStats">
                    <div className="dashboardQuickStat">
                      <Calendar size={16} />
                      <div>
                        <span className="dashboardQuickStatLabel">Founded</span>
                        <span className="dashboardQuickStatValue">{b2bProfile?.yearFounded || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="dashboardQuickStat">
                      <Users size={16} />
                      <div>
                        <span className="dashboardQuickStatLabel">Employees</span>
                        <span className="dashboardQuickStatValue">{b2bProfile?.numberOfEmployees || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="dashboardQuickStat">
                      <Package size={16} />
                      <div>
                        <span className="dashboardQuickStatLabel">Production</span>
                        <span className="dashboardQuickStatValue">{b2bProfile?.productionCapacity || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="dashboardQuickStat">
                      <TrendingUp size={16} />
                      <div>
                        <span className="dashboardQuickStatLabel">Orders</span>
                        <span className="dashboardQuickStatValue">{b2bProfile?.fulfilledOrders || 0}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="dashboardSellerInfoContainer">
                  {!isBusiness && (
                    <div className="dashboardSellerInfoSection">
                      <div className="dashboardInfoIcon"><FaLanguage /></div>
                      <div className="dashboardInfoContent">
                        <h3>Languages</h3>
                        <p>{user.languages}</p>
                      </div>
                    </div>
                  )}
                  <div className="dashboardSellerInfoSection">
                    <div className="dashboardInfoIcon">
                      <Star className="dashboardVendorStarIcon" fill="currentColor" />
                    </div>
                    <div className="dashboardInfoContent">
                      <h3>Rating</h3>
                      <div className="dashboardSellerRatingContainer">
                        <span className="dashboardVendorRatingValue">{sellerDetails?.rating.toFixed(1)}</span>
                        <span className="dashboardVendorTotalReviews">({sellerDetails?.noOfReviews})</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="actionButtonContainer">
                  <button
                    onClick={() => navigate(`/vendorDetail/${sellerDetails._id}`, { state: { isSellerAccount: true } })}
                    className="primaryBtn3" style={{ marginBottom: '10px' }}>Preview Profile</button>
                  <button
                    onClick={() => navigate('/seller/settings', { state: { isSellerAccount: true } })}
                    className="primaryBtn">Edit Profile</button>
                </div>
              </div>
            </div>

            {/* ── Business Capabilities (business sellers only) ── */}
            {isBusiness && (
              <div className="dashboardInfoCard">
                <h3 className="dashboardInfoCardTitle">Business Capabilities</h3>
                <div className="dashboardInfoCardContent">
                  {b2bProfile?.website && (
                    <div className="dashboardInfoItem">
                      <Globe className="dashboardInfoIcon" size={20} />
                      <div className="dashboardInfoDetails">
                        <span className="dashboardInfoLabel">Website</span>
                        <a href={b2bProfile.website} target="_blank" rel="noreferrer" className="dashboardInfoLink">{b2bProfile.website}</a>
                      </div>
                    </div>
                  )}
                  {b2bProfile?.inventoryCapacity && (
                    <div className="dashboardInfoItem">
                      <Warehouse className="dashboardInfoIcon" size={20} />
                      <div className="dashboardInfoDetails">
                        <span className="dashboardInfoLabel">Inventory Capacity</span>
                        <span className="dashboardInfoValue">{b2bProfile.inventoryCapacity}</span>
                      </div>
                    </div>
                  )}
                  {b2bProfile?.exportMarkets?.length > 0 && (
                    <div className="dashboardInfoItem">
                      <Globe className="dashboardInfoIcon" size={20} />
                      <div className="dashboardInfoDetails">
                        <span className="dashboardInfoLabel">Export Markets</span>
                        <span className="dashboardInfoValue">{b2bProfile.exportMarkets.join(', ')}</span>
                      </div>
                    </div>
                  )}
                  {b2bProfile?.certifications?.length > 0 && b2bProfile.certifications.some(c => c.name) && (
                    <div className="dashboardInfoItem">
                      <Award className="dashboardInfoIcon" size={20} />
                      <div className="dashboardInfoDetails">
                        <span className="dashboardInfoLabel">Certifications</span>
                        <span className="dashboardInfoValue">{b2bProfile.certifications.map(c => c.name).filter(Boolean).join(', ')}</span>
                      </div>
                    </div>
                  )}
                  <div className="dashboardInfoItem">
                    <div className="dashboardInfoIcon"><FaLanguage /></div>
                    <div className="dashboardInfoDetails">
                      <span className="dashboardInfoLabel">Languages</span>
                      <span className="dashboardInfoValue">{user.languages}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Contact Info ── */}
            <div className="dashboardInfoCard">
              <h3 className="dashboardInfoCardTitle">Contact Info</h3>
              <div className="dashboardInfoCardContent">
                {isBusiness && b2bProfile?.telephone?.filter(Boolean).length > 0 ? (
                  b2bProfile.telephone.filter(Boolean).map((tel, idx) => (
                    <div key={idx} className="dashboardInfoItem">
                      <Phone className="dashboardInfoIcon" size={20} />
                      <div className="dashboardInfoDetails">
                        <span className="dashboardInfoLabel">{idx === 0 ? 'Primary Phone' : 'Secondary Phone'}</span>
                        <span className="dashboardInfoValue">{tel}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="dashboardInfoItem">
                    <Phone className="dashboardInfoIcon" size={20} />
                    <div className="dashboardInfoDetails">
                      <span className="dashboardInfoLabel">Phone No:</span>
                      <span className="dashboardInfoValue">{user?.phone}</span>
                      {user?.secondaryPhone && <span className="dashboardInfoValue">{user?.secondaryPhone}</span>}
                    </div>
                  </div>
                )}
                <div className="dashboardInfoItem">
                  <Mail className="dashboardInfoIcon" size={20} />
                  <div className="dashboardInfoDetails">
                    <span className="dashboardInfoLabel">Email</span>
                    <span className="dashboardInfoValue">{user?.email}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Address (canonical: user.buyerAddress, synced with seller) ── */}
            <div className="dashboardInfoCard">
              <h3 className="dashboardInfoCardTitle">Location</h3>
              <div className="dashboardInfoCardContent">
                <div className="dashboardInfoItem">
                  <MapPin className="dashboardInfoIcon" size={20} />
                  <div className="dashboardInfoDetails">
                    <span className="dashboardInfoLabel">Address</span>
                    <span className="dashboardInfoValue">
                      {accountLocationFull || '—'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT SECTION ── */}
          <div className="dashboardVendorDetailRightSection">
            <div className="dashboardPostingsContainer">
              <div className="dashboardPostingsHeader">
                <h2 className='primaryHeadingSmall'>Your Postings</h2>
                <button onClick={() => navigate('/createProduct/new')} className="modalButton submit">
                  Create New Listing
                </button>
              </div>
              <ScrollableRow
                items={products}
                itemsPerRow={3}
                renderItem={(product) => <ProductCard key={product._id} product={product} linkToSellerView />}
                isLoading={isLoadingProducts}
                noItemsMessage={'No products available.'}
              />
            </div>

            <div className="dashboardPostingsContainer dashboardB2BListingsSection">
              <div className="dashboardPostingsHeader">
                <h2 className='primaryHeadingSmall'>Your B2B Listings</h2>
                {canCreateB2B && (
                  <button onClick={() => navigate('/createProduct/new?market=b2b')} className="modalButton submit">
                    Create B2B Listing
                  </button>
                )}
              </div>

              {isLoadingB2B ? (
                <Loader type="simpleMini" />
              ) : !b2bProfile ? (
                <div className="dashboardB2BStatusCard dashboardB2BStatusNeutral">
                  <h3>Become a Business Seller to Unlock B2B</h3>
                  <p>Your account is currently registered as an individual seller. Complete your business profile to sell on the B2B marketplace.</p>
                  <button className="primaryBtn" onClick={() => navigate('/becomeSeller?mode=b2b-upgrade')}>Become a Business Seller</button>
                </div>
              ) : b2bProfile?.status === 'pending' ? (
                <div className="dashboardB2BStatusCard dashboardB2BStatusPending">
                  <h3>Business Profile Under Review</h3>
                  <p>Your business seller request is pending admin review. You will be able to create B2B listings once approved.</p>
                </div>
              ) : b2bProfile?.status === 'declined' ? (
                <div className="dashboardB2BStatusCard dashboardB2BStatusDeclined">
                  <h3>Business Profile Needs Updates</h3>
                  <p>Your previous business seller request was declined. Update your details and resubmit for review.</p>
                  <button className="primaryBtn" onClick={() => navigate('/becomeSeller?mode=b2b-upgrade')}>Update Business Profile</button>
                </div>
              ) : (
                <ScrollableRow
                  items={b2bListings}
                  itemsPerRow={3}
                  renderItem={(listing) => <ProductCard key={listing._id} b2bListing={listing} linkToSellerView />}
                  isLoading={false}
                  noItemsMessage={'No B2B listings available yet.'}
                />
              )}
            </div>

            <div className="activeOrdersContainer">
              <div className='sellerDashboardActiveOrdersUpper'>
                <h2 className='primaryHeadingSmall' style={{ marginBottom: "10px" }}>Active Orders</h2>
                <Link className="seeAllBtn" to="/seller/orders">View All <FaArrowRight /></Link>
              </div>
              {isLoadingOrders ? (
                <Loader type="simpleMini" />
              ) : recentActiveOrders.length > 0 ? (
                <table className="ordersMainTable">
                  <thead>
                    <tr className="ordersTableHeader">
                      <th>Product</th>
                      <th>Order #</th>
                      <th>Order placed on</th>
                      <th>Amount to Get</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActiveOrders.map((order) => (
                      <tr key={order._id} className="ordersTableRow">
                        <td>
                          <div className="ordersSellerCell">
                            <img src={order.productInfo.thumbnail} alt={order.productInfo.title} className="ordersProductAvatar" />
                            <span className="ordersProductName singleLineSmallText">{order.productInfo.title}</span>
                          </div>
                        </td>
                        <td>{order.customOrderId}</td>
                        <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                        <td>{formatCurrencyDual(amountToGet(order.summary), currency, exchangeRate)}</td>
                        <td><span className={getStatusStyle(order.currentStatus)}>{order.currentStatus}</span></td>
                        <td>
                          <button className="ordersViewDetailsButton" onClick={() => navigateTo(order)}>
                            View Details <FaChevronRight size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No active orders</p>
              )}
            </div>

            <div className='myReviewsHistoryContainer'>
              <h2 className='dashboardPrimaryHeadingSmall' style={{ marginBottom: "10px" }}>Reviews</h2>
              <p className="dashboardReviewsHint">
                Showing the 5 most recent reviews from buyers.{' '}
                <Link to="/seller/myReviews?tab=history">Open Ratings & feedback</Link>
                {' '}to see your full history.
              </p>
              {mappedReviews.length < 1 ? <p>No reviews yet</p> : <ReviewHistoryContainer reviews={mappedReviews} isMyReview={false} isProductReview={true} />}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SellerDashboard;
